import {observer} from 'mobx-react-lite'
import React, {useContext, useEffect, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Input} from '@components/common/base/input'
import {Sort, Table} from '@components/common/base/table'
import {cn} from '@containers/html-utils'
import {createColumns} from './create-columns'
import {UserTablesProps} from './types'
import {
  PropsContext,
  UsersTableContext,
  UsersTableStore,
  useUsersTableStore
} from './users-table-store'

export const UsersTable = (props: UserTablesProps) => {
  const [store] = useState(() => new UsersTableStore(props))

  return (
    <PropsContext.Provider value={props}>
      <UsersTableContext.Provider value={store}>
        <UsersTableBase />
      </UsersTableContext.Provider>
    </PropsContext.Provider>
  )
}

export const UsersTableBase = observer(() => {
  const props = useContext(PropsContext)
  const [state, store] = useUsersTableStore()

  useEffect(() => {
    store.init(props)
  }, [props.users, props.selected, props.singleMode])

  const columns = createColumns(store, state, props)
  const defaultSortColumnIndex = columns.findIndex(x => x.defaultSort)

  const getDefaultSort = (): Sort | undefined => {
    if (props.disableDefaultSort) {
      return undefined
    }
    return props.selected.length
      ? undefined
      : {columnIndex: defaultSortColumnIndex, order: false}
  }

  const noUsersFound = store.filteredUsers.length === 0
  const rows = store.filteredUsers.map(data => ({
    data,
    className: cn(
      !props.disableClickableRows && 'cursor-pointer',
      'group',
      'h-48'
    ),
    columnsWrapperClassName: 'px-16 hover:bg-interactive-l100',
    onClick: () => {
      if (!props.disableClickableRows) {
        store.toggleSelected(data.userId)
        props.onToggled?.([data.userId])
        props.onSelectedChanged?.([...state.selected])
      }
    }
  }))

  return (
    <div className={cn(props.className, 'flex flex-col')}>
      <div className="mb-12">
        <Input
          icon="IconSearch"
          className="w-full"
          placeholder={store.searchPlaceholder}
          value={state.searchQuery}
          onChange={e => store.setSearchQuery(e.text)}
        />
      </div>
      <Table
        noBorders
        scrollDirection="xy"
        defaultSort={getDefaultSort()}
        className="overflow-y-auto cursor-default h-full"
        underHeadElement={props.underHeadElement}
        rows={rows}
        noFoundElement={
          noUsersFound && (
            <TwNoUserFound>{Lang.msg.usersTable.noUserFound}</TwNoUserFound>
          )
        }
        columns={columns}
      />
    </div>
  )
})

const TwNoUserFound = tw.div`
  plain-text-regular 
  text-placeholder 
  ml-48
  pt-16
`
